<template>
  <div :class="$style.container" data-testid="price-trends-is24">
    <HgCardFlat>
      <div v-if="isLoading" :class="$style.loadingWrapper" data-testid="price-trends-skeleton">
        <div>
          <HgSkeleton height="30px" width="220px" />
          <HgSkeleton
            v-if="$slots.subtitle"
            :class="$style.subtitleSkeleton"
            height="21px"
            width="220px"
          />
        </div>
        <HgSkeleton height="120px" />
        <HgSkeleton height="50px" />
        <HgSkeleton v-if="$slots.summary" height="50px" />
      </div>
      <template v-else>
        <div>
          <h3 :class="$style.heading">{{ $t('localLanding.priceTrends.title') }}</h3>
          <p v-if="$slots.subtitle" :class="$style.subtitle"><slot name="subtitle" /></p>
          <HgIcon
            v-if="hasModal"
            icon="Info"
            color="black"
            :class="$style.infoIcon"
            data-testid="info-icon"
            @click="$emit('onInfoIconClick')"
          />
        </div>
        <NoDataView v-if="!series[0]?.data?.length" :class="$style.noDataView" />
        <div :class="$style.chartWrapper">
          <ApexChart
            v-if="isMounted"
            :type="chartType"
            :options="chartOptions"
            :series="series"
            :class="$style.chart"
            :height="chartHeight"
          />
          <HgSkeleton v-else height="170px" />
          <HgSegmentedButton
            v-model="selectedRange"
            :disabled="!isMounted || !series[0]?.data?.length"
            :class="$style.segmentedButton"
            name="yearRangeSelector"
            :options="selections"
            @update:modelValue="handleUpdatedRangeIs24"
          />
        </div>
        <div v-if="$slots.summary" :class="$style.summary">
          <slot name="summary" />
        </div>
      </template>
    </HgCardFlat>
  </div>
</template>

<script setup lang="ts">
import { usePriceTrends } from '@/components/common/PriceTrends/usePriceTrends';
import NoDataView from '@/components/property-details-page/NoDataView/NoDataView.vue';
import { PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { MAX_TICK_AMOUNT, PriceTrendsRangeSummary, PropertyType } from '@/types';
import { HgCardFlat, HgIcon, HgSegmentedButton, HgSkeleton } from '@smg-real-estate/domus';
import { HgSegmentedOptions, HgSelectOptions } from '@smg-real-estate/domus/index';
import { ApexOptions } from 'apexcharts';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { VueApexChartsComponentProps } from 'vue3-apexcharts';

const ApexChart = defineAsyncComponent(() => import('vue3-apexcharts'));
type ApexChartType = NonNullable<VueApexChartsComponentProps['type']>;

const {
  priceTrendsData,
  propertyType,
  isLoading = true,
  hasModal = false,
} = defineProps<{
  priceTrendsData: PriceEvolutionResponse | null;
  propertyType: PropertyType;
  isLoading: boolean;
  hasModal?: boolean;
}>();

const emit = defineEmits<{
  onInfoIconClick: [];
  onRangeChange: [value: PriceTrendsRangeSummary, isInitial: boolean];
}>();

const selectedRange = ref(0);
const chartType = ref<ApexChartType>('area');
const chartHeight = ref(205);
const is24Colors = ref({
  //matches color token from Domus
  teal_90: '#3fffc9',
  teal_140: '#00627a',
});

const priceTrendsDataRef = computed(() => priceTrendsData);
const propertyTypeRef = computed(() => propertyType);

const {
  handleUpdatedRangeIs24,
  handleRangeChangeIs24,
  isMounted,
  series,
  categories,
  min,
  max,
  selections,
  getXTextByIndex,
} = usePriceTrends({
  selectedRange,
  priceTrendsData: priceTrendsDataRef,
  propertyType: propertyTypeRef,
  onRangeChange: (value, isInitial) => emit('onRangeChange', value, isInitial),
});

const chartOptions = computed<ApexOptions>(() => ({
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  fill: {
    type: 'solid',
    opacity: 0.2,
    colors: [is24Colors.value.teal_90],
  },
  stroke: {
    curve: 'straight',
    width: 2,
    colors: [is24Colors.value.teal_140],
  },
  markers: {
    hover: {
      size: 0,
    },
  },
  xaxis: {
    type: 'category',
    overwriteCategories: categories.value,
    tooltip: {
      enabled: false,
    },
    labels: {
      offsetX: -2,
    },
    crosshairs: {
      width: 1,
      stroke: {
        width: 1,
        dashArray: 0,
      },
      fill: {
        type: 'solid',
        color: is24Colors.value.teal_140,
      },
    },
  },
  yaxis: {
    labels: {
      formatter: (val: number) => Math.round(val) + '%',
    },
    min: min.value,
    max: max.value,
    tickAmount: tickAmount.value,
  },
  tooltip: {
    custom: ({ dataPointIndex, seriesIndex, series }) => {
      const formattedXValue = getXTextByIndex(dataPointIndex);
      const yValue = series[seriesIndex][dataPointIndex];
      return `<div class="tooltip-wrapper">
                      <p class="tooltip-content-x">${formattedXValue}</p>
                      <p class="tooltip-content-y">${yValue}%</p>
                    </div>`;
    },
  },
}));
const tickAmount = computed<number>(() =>
  max.value - min.value < MAX_TICK_AMOUNT ? Math.ceil(max.value - min.value) : MAX_TICK_AMOUNT,
);

watch(
  selections,
  (value: HgSelectOptions[] | HgSegmentedOptions[]) => {
    const range = value?.[0]?.value ?? 0;
    selectedRange.value = +range;
    handleRangeChangeIs24(+range, true);
  },
  { immediate: true },
);
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  height: 100%;
  padding: 0;
  color: $color-grey-100;

  [class*='HgCardFlat_cardFlat'] {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: inherit;
    padding: $space-150;
    border: unset;
    box-shadow: $elevation-minimal;
  }
}

.noDataView {
  position: absolute;
  top: 30%;
  left: 30%;
  z-index: 1;
}

.chartWrapper {
  @include vertical-margin($space-150);
}

.loadingWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.subtitleSkeleton {
  margin-top: $space-75;
}

.card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: inherit;
  padding: $space-150;
  border: unset;
  box-shadow: $elevation-minimal;
}

.heading {
  @include heading-3;
  @include vertical-margin;
}

.subtitle {
  @include vertical-margin;
  @include body-3;
}

.infoIcon {
  position: absolute;
  z-index: 1;
  right: $space-150;
  top: $space-200;
  cursor: pointer;
}

.summary {
  @include body-2($color-grey-100);
}

.chart {
  width: calc(100% + $space-50);
  height: calc(100% - $space-200);
  margin-left: -$space-50;
  margin-top: -$space-200;

  [class*='apexcharts-tooltip'] {
    &[class*='apexcharts-theme-light'] {
      top: 20px !important;
      background: rgb($color-white, 0.8);
      border: none;
      border-radius: unset;
      box-shadow: unset;
    }
  }

  [class='tooltip-wrapper'] {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    width: $space-500;
    padding: $space-25;
  }

  [class='tooltip-content-x'] {
    @include vertical-margin;
    @include body-3;
  }

  [class='tooltip-content-y'] {
    @include vertical-margin;
    @include body-3-bold;
  }
}

.segmentedButton {
  width: 100%;

  [class*='HgSegmentedButton_widget'] {
    padding-inline: $space-25;
    background-color: $color-grey-30;

    &[class*='HgSegmentedButton_primary'] [class*='HgSegmentedButton_active'] {
      &:not([class*='HgSegmentedButton_disabled']) {
        color: $color-grey-100;
        background-color: $color-white;
        border-radius: $border-radius;
      }
    }

    [class*='HgSegmentedButton_option'] {
      position: relative;
      padding-block: $space-25;
      margin: $space-25 0;
      background-color: inherit;
      border: unset;

      @include body-2-bold($color-grey-90);

      & {
        line-height: 1.25;
      }

      &:not([class*='HgSegmentedButton_active'])
        + :not([class*='HgSegmentedButton_active'])::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 1px;
        content: '';
        background-color: $color-grey-60;
      }
    }
  }
}
</style>
